import { useCallback } from "react";
import { create } from "zustand";

type InflightMap = Record<string, number>;
type LibraryPanelInflightState = {
  inflightByCatalogId: InflightMap;
  actions: {
    incrementInflightForCatalogId: (catalogObjectId: string) => void;
    decrementInflightForCatalogId: (catalogObjectId: string) => void;
  };
};

const useLibraryPanelInflightStore = create<LibraryPanelInflightState>((set) => ({
  inflightByCatalogId: {} as InflightMap,
  actions: {
    incrementInflightForCatalogId: (catalogObjectId: string) =>
      set((state: LibraryPanelInflightState) => ({
        inflightByCatalogId: {
          ...state.inflightByCatalogId,
          [catalogObjectId]: (state.inflightByCatalogId[catalogObjectId] ?? 0) + 1,
        },
      })),
    decrementInflightForCatalogId: (catalogObjectId: string) =>
      set((state: LibraryPanelInflightState) => {
        const next = { ...state.inflightByCatalogId };
        const n = (next[catalogObjectId] ?? 1) - 1;
        if (n <= 0) delete next[catalogObjectId];
        else next[catalogObjectId] = n;
        return { inflightByCatalogId: next };
      }),
  },
}));

export const getLibraryPanelInflightActions = () => useLibraryPanelInflightStore.getState().actions;

/** Subscribes to one catalog id's counter; skips re-render when another library tile loads. */
export function useLibraryPanelInflightCount(catalogObjectId: string | undefined) {
  return useLibraryPanelInflightStore(
    useCallback(
      (state) =>
        catalogObjectId != null && catalogObjectId !== ""
          ? (state.inflightByCatalogId[catalogObjectId] ?? 0)
          : 0,
      [catalogObjectId]
    )
  );
}

export function useLibraryPanelAnyCatalogInflight() {
  return useLibraryPanelInflightStore((state) =>
    Object.values(state.inflightByCatalogId).some((n) => typeof n === "number" && n > 0)
  );
}
